import type { ChangeEvent } from 'react'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'

export interface RobotEventFilterValues {
  keyword: string
  status: number | undefined
  eventType: number | undefined
  time: DateRangeValue | undefined
}

interface RobotEventFilterPanelProps {
  values: RobotEventFilterValues
  onChange: (values: RobotEventFilterValues) => void
  onSearch: () => void
}

export function RobotEventFilterPanel({
  values,
  onChange,
  onSearch,
}: RobotEventFilterPanelProps) {
  const updateFilter = <TKey extends keyof RobotEventFilterValues>(
    key: TKey,
    value: RobotEventFilterValues[TKey],
  ) => {
    onChange({ ...values, [key]: value })
  }

  return (
    <FilterFormWrapper
      onSearch={onSearch}
      className="rounded-xl border border-gray-200 bg-white px-6 py-5"
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[200px] flex-[2] flex-col gap-1">
          <span className="text-xs">Tên sự kiện</span>
          <Input
            placeholder="Nhập từ khóa"
            value={values.keyword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              updateFilter('keyword', e.target.value)
            }
          />
        </div>

        <div className="flex min-w-[200px] flex-[1] flex-col gap-1">
          <span className="text-xs">Loại sự kiện</span>
          <Select
            placeholder="Tất cả"
            value={values.eventType}
            className="w-full"
            onValueChange={(value) => updateFilter('eventType', value)}
            allowClear
            options={[
              { value: 1, label: 'Khẩn cấp' },
              { value: 2, label: 'Lập lịch' },
            ]}
          />
        </div>

        <div className="flex min-w-[200px] flex-[0.8] flex-col gap-1">
          <span className="text-xs">Trạng thái sự kiện</span>
          <Select
            placeholder="Tất cả"
            value={values.status}
            className="w-full"
            onValueChange={(value) => updateFilter('status', value)}
            allowClear
            options={[
              { value: 1, label: 'Hoạt động' },
              { value: 0, label: 'Không hoạt động' },
            ]}
          />
        </div>

        <div className="flex min-w-[200px] flex-[1.5] flex-col gap-1">
          <span className="text-xs">Thời gian</span>
          <DateRangePicker
            value={values.time}
            onChange={(value) => updateFilter('time', value)}
            className="w-full"
          />
        </div>

        <Button
          variant="outline"
          size="md"
          className="shrink-0 gap-2 border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
          onClick={onSearch}
        >
          <Search size={16} />
          Tìm kiếm
        </Button>
      </div>
    </FilterFormWrapper>
  )
}
